/**
 * Small display helpers — **locale-neutral by construction**.
 *
 * Nothing here returns a sentence. `timeAgo` and `duration` return the numbers
 * and the *band* those numbers fall into; the words come from the `ios_ago_*`
 * keys at the call site. That split is what lets these live in `src/lib`, which
 * is React-free and DOM-free and runs in the node test environment `vite.config.ts`
 * pins — a translated string here would need `t`, and `t` lives behind a React
 * context.
 *
 * It is also what makes the bands testable at all. They used to be pinned in
 * hard-coded Vietnamese, so `LastCheckBanner` re-implemented the same boundaries
 * inline with a note asking the next person to keep the two copies in step by
 * hand: the tested copy had no callers and the live copy had no tests. The
 * boundaries below are the single copy, and the `Math.max(0, …)` guard that
 * keeps a phone whose clock jumped from reporting a negative age is part of it.
 */

/**
 * How long ago something happened, at the coarseness a person standing over a
 * field device actually reads.
 *
 * Five bands, not a raw number of seconds. Under ten seconds nobody wants a
 * count; past an hour the minutes still matter ("2 h 5 min ago" is a different
 * story from "2 h ago" when the interval is hourly), which is why `hours` and
 * `days` carry a `rest`.
 */
export type TimeAgo =
  | { unit: 'just-now' }
  | { unit: 'seconds'; value: number }
  | { unit: 'minutes'; value: number }
  /** `rest` is the leftover minutes. */
  | { unit: 'hours'; value: number; rest: number }
  /** `rest` is the leftover hours. */
  | { unit: 'days'; value: number; rest: number };

export function timeAgo(from: number, now: number = Date.now()): TimeAgo {
  // Floored at zero deliberately. A field phone that syncs its clock mid-cycle
  // can put `from` in the future, and "-3 s ago" on the dashboard reads as a bug
  // in the app rather than as the clock jump it is.
  const seconds = Math.max(0, Math.round((now - from) / 1000));
  if (seconds < 10) return { unit: 'just-now' };
  if (seconds < 60) return { unit: 'seconds', value: seconds };
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return { unit: 'minutes', value: minutes };
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return { unit: 'hours', value: hours, rest: minutes % 60 };
  const days = Math.floor(hours / 24);
  return { unit: 'days', value: days, rest: hours % 24 };
}

/**
 * A countdown to the next cycle, in the same two bands.
 *
 * `rest` is kept even when it is zero rather than collapsing to a bare minute
 * count here: whether "3 min" or "3 min 0 s" reads better is a wording decision,
 * and wording belongs to whoever owns the string table.
 */
export type Duration =
  | { unit: 'seconds'; value: number }
  | { unit: 'minutes'; value: number; rest: number };

export function duration(ms: number): Duration {
  const seconds = Math.max(0, Math.round(ms / 1000));
  if (seconds < 60) return { unit: 'seconds', value: seconds };
  return { unit: 'minutes', value: Math.floor(seconds / 60), rest: seconds % 60 };
}

/**
 * `"14:32:07"` — local wall clock, which is what an operator compares against.
 *
 * Takes the active locale tag rather than hard-coding one: this app ships four
 * languages, and a clock formatted for a language nobody on the device reads is
 * the same class of bug as an untranslated label. `hour12: false` is the part
 * that must not change — a field report timed "2:32" is ambiguous by half a day.
 */
export function clockTime(at: number, locale: string): string {
  return clockFor(locale).format(new Date(at));
}

/**
 * One formatter per language, built once.
 *
 * Same reasoning as the cached `Intl.NumberFormat` in `src/i18n/index.tsx`:
 * constructing an `Intl` formatter is the expensive part, and this runs on
 * every render of a banner that ticks once a second.
 */
const clocks = new Map<string, Intl.DateTimeFormat>();

function clockFor(locale: string): Intl.DateTimeFormat {
  const cached = clocks.get(locale);
  if (cached) return cached;
  const built = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  clocks.set(locale, built);
  return built;
}

/**
 * How stale the last successful report is (SPEC §11).
 *
 * Three bands rather than a raw number, because the question a person is asking
 * when they pick this phone up is "is it still working?" and a timestamp makes
 * them do the arithmetic. `stale` starts at two missed cycles — one missed cycle
 * is a network blip, two is a device that stopped.
 */
export function freshness(
  lastSuccessAt: number | null,
  intervalMinutes: number,
  now: number = Date.now(),
): 'never' | 'fresh' | 'late' | 'stale' {
  if (lastSuccessAt === null) return 'never';
  const age = now - lastSuccessAt;
  const period = Math.max(1, intervalMinutes) * 60_000;
  if (age <= period * 1.25) return 'fresh';
  if (age <= period * 2) return 'late';
  return 'stale';
}
